import { Box, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import { isNil } from "lodash-es";
import { useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AppIconButton from "@/common/components/Button/AppIconButton";
import DataTabular from "@/common/components/DataTabular/DataTabular";
import Datetime from "@/common/components/Datetime/Datetime";
import { RepairOperationAddVehicleDamagesModal } from "@/common/components/Entity/RepairOperation/RepairOperationAddVehicleDamagesModal";
import VehicleDamageLink from "@/common/components/Entity/VehicleDamage/VehicleDamageLink";
import { VisualModelViewerOfDamageDetection } from "@/common/components/Entity/VehicleVisualModel/Viewer/v2/Entity/VisualModelViewerOfDamageDetection";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import InfoTile from "@/common/components/Form/Display/InfoTile";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppMenuItem from "@/common/components/Menu/AppMenuItem";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import BaseEditExternalNumberModal from "@/common/components/Modals/common/BaseEditExternalNumberModal";
import BaseEditMileageModal from "@/common/components/Modals/common/BaseEditMileageModal";
import BaseEditNotesModal from "@/common/components/Modals/common/BaseEditNotesModal";
import AppTypography from "@/common/components/Text/AppTypography";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { ROUTE_PATH } from "@/common/constants/routing";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ViewVariant } from "@/common/ts/layout";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import { AppPermission, DamageDetectionDto, EntityType } from "@/core/api/generated/v1";

import ContractLink from "../../../Contract/ContractLink";
import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import GeneralInspectorDisplay from "../../../General/Display/GeneralInspectorDisplay";
import VehicleLink from "../../../Vehicle/VehicleLink";
import { DamageDetectionCostEvaluationOverviewDisplay } from "../../DamageDetectionCostEvaluationOverviewDisplay";

interface Props {
  damageDetection: DamageDetectionDto;
  onUpdate: (newValue: Nil<DamageDetectionDto>) => void;
}

export default function OverviewTabContent({ damageDetection, onUpdate }: Props) {
  const { enqueueSnackbar } = useAppSnackbar();
  const history = useAppHistory();

  const [isUpdateMileageModalOpened, setIsUpdateMileageModalOpened] = useState(false);

  const [isAddToRepairOperationModalOpen, setIsAddToRepairOperationModalOpen] = useState(false);
  const [damageIdToAddToRepairOperation, setDamageIdToAddToRepairOperation] = useState<
    string | undefined
  >(undefined);
  // const visualModelRequest = useApiRequest(
  //   apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsModelIdGet,
  //   {
  //     modelId: damageDetection?.vehicleVisualModel?.id || "",
  //   },
  //   {
  //     deps: [damageDetection.id],
  //     skip: !damageDetection?.vehicleVisualModel?.id,
  //   },
  // );
  // const visualModel = visualModelRequest?.data;

  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityChangedByInfoCard entity={damageDetection} />}
          />
        }
      >
        <ViewContentBlock spacing={2}>
          {/* <Stack direction={{ xxs: "column", md: "row" }} alignItems='flex-start' spacing={2}>
            <VehicleBlock
              sx={{ maxWidth: { md: "400px" } }}
              variant='reference'
              vehicle={damageDetection.vehicle}
              withDetailsToggle
              withLink
            />

            <ContractBlock
              sx={{ maxWidth: { md: "400px" } }}
              variant='reference'
              contract={damageDetection.contract}
              withDetailsToggle
              withLink
            />
          </Stack> */}

          <Stack gap={1}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              {/* <InfoTile
              label='Number'
              iconOf='localNumber'
              isEmpty={isNil(damageDetection?.localNumber)}
            >
              {damageDetection?.localNumber}
            </InfoTile> */}

              <InfoTile label='Vehicle' iconOf='vehicle' isEmpty={isNil(damageDetection?.vehicle)}>
                <VehicleLink entity={damageDetection?.vehicle} />
              </InfoTile>

              <InfoTile
                label='Contract'
                iconOf='contract'
                isEmpty={isNil(damageDetection?.contract)}
              >
                <ContractLink entity={damageDetection?.contract} />
              </InfoTile>

              <InfoTile label='Cost evaluations' iconOf='damageCostEvaluation' isEmpty={false}>
                <DamageDetectionCostEvaluationOverviewDisplay
                  variant='compact'
                  damageDetection={damageDetection}
                  overview={damageDetection?.costEvaluationOverview}
                />
              </InfoTile>

              <InfoTile
                label='External number'
                iconOf='externalNumber'
                isEmpty={!damageDetection.externalNumber}
                endAdornment={
                  <BaseEditExternalNumberModal
                    initialValues={{ externalNumber: damageDetection.externalNumber }}
                    onConfirm={async (externalNumber) => {
                      const response =
                        await apiClient.damageDetectionsApi.apiV1DamageDetectionsDamageDetectionIdExternalNumberPut(
                          {
                            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                            damageDetectionId: damageDetection.id || "",
                            generalExternalNumberInputDto: { externalNumber },
                          },
                        );
                      onUpdate(response.data);
                    }}
                  />
                }
              >
                {damageDetection.externalNumber}
              </InfoTile>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              <InfoTile
                label='Inspected at'
                iconOf='date'
                isEmpty={isNil(damageDetection?.inspectedAt)}
              >
                <Datetime datetime={damageDetection?.inspectedAt} />
              </InfoTile>

              <InfoTile
                label='Inspector'
                iconOf='inspector'
                isEmpty={isNil(damageDetection?.inspector)}
              >
                <GeneralInspectorDisplay
                  inspector={damageDetection?.inspector}
                  inlineUserProps={{ withLink: true, withAvatar: true, withPoper: true }}
                />
              </InfoTile>

              <InfoTile
                label='Notes'
                iconOf='notes'
                isEmpty={!damageDetection.notes}
                endAdornment={
                  <BaseEditNotesModal
                    initialValues={{ notes: damageDetection.notes }}
                    onConfirm={async (notes) => {
                      const response =
                        await apiClient.damageDetectionsApi.apiV1DamageDetectionsDamageDetectionIdNotesPut(
                          {
                            nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                            damageDetectionId: damageDetection.id || "",
                            generalNotesInputDto: { notes },
                          },
                        );
                      onUpdate(response.data);
                    }}
                  />
                }
              >
                {damageDetection.notes}
              </InfoTile>

              {/* <InfoTile
                label={
                  <AppTypography
                    decoration={{ variant: "helpText" }}
                    tooltipProps={{
                      title: "Sent to external damage cost evaluation via C2C request.",
                    }}
                  >
                    Sent to assessment TODO: remove
                  </AppTypography>
                }
                iconOf='data'
                isEmpty={isNil(damageDetection?.isSentToExternalCostEvaluation)}
              >
                <BooleanValue value={damageDetection?.isSentToExternalCostEvaluation} />
              </InfoTile> */}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              <InfoTile
                label='Mileage'
                iconOf='gauge'
                isEmpty={!damageDetection.mileage}
                endAdornment={
                  <BaseEditMileageModal
                    vehicleId={damageDetection.vehicle?.id}
                    mileage={damageDetection.mileage}
                    trigger={
                      <AppIconButton
                        sx={{ p: "2px", ml: 0.5 }}
                        onClick={() => setIsUpdateMileageModalOpened(true)}
                      >
                        <AppIcon of='edit' sx={{ width: "0.8em", height: "0.8em" }} />
                      </AppIconButton>
                    }
                    triggerParams={{ isDisableDefaultAction: true }}
                    open={isUpdateMileageModalOpened}
                    onClose={() => setIsUpdateMileageModalOpened(false)}
                    onConfirm={async (mileage) => {
                      await apiClient.damageDetectionsApi.apiV1DamageDetectionsDamageDetectionIdMileagePut(
                        {
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          damageDetectionId: damageDetection.id || "",
                          updateVehicleMileageDto: {
                            vehicleId: damageDetection.vehicle?.id,
                            mileage: mileage,
                          },
                        },
                      );
                    }}
                    onMileageChanged={() => onUpdate(null)}
                  />
                }
              >
                {damageDetection.mileage?.mileage} {damageDetection.mileage?.unit}
              </InfoTile>

              <InfoTile
                label='Fuel level'
                iconOf='fuel'
                isEmpty={isNil(damageDetection?.fuelLevel)}
              >
                {damageDetection?.fuelLevel}
              </InfoTile>
            </Box>
          </Stack>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.DamageDetection}
          entityId={damageDetection.id}
          entity={damageDetection}
          entityUpdatePermission={AppPermission.DamageDetectionManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={() => onUpdate(null)}
        />

        <ViewContentBlock>
          <Stack direction='row' justifyContent='space-between'>
            <Typography component='div' variant='h2'>
              Damages
            </Typography>
          </Stack>

          <VisualModelViewerOfDamageDetection entity={damageDetection} />
        </ViewContentBlock>

        {!ArrayHelper.isEmpty(damageDetection.attachments) && (
          <ViewContentBlock>
            <FileListView
              files={FileItem.createManyFrom(damageDetection.attachments)}
              maxVisibleFiles={10}
              itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
              itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
            />
          </ViewContentBlock>
        )}

        {/* Items */}
        <ViewContentBlock>
          <DataTabular
            columns={[
              {
                field: "partType.name",
                title: "Part type",
                flex: 1,
                renderCell: (item) => (
                  <Stack>
                    <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
                      <Box>{item.partType?.name}</Box>
                      <Typography component='span' variant='body2' color='text.secondary'>
                        (<InlineApiEnumValue type='VehicleArea' value={item.area} /> /{" "}
                        {(item.projection && (
                          <InlineApiEnumValue type='VehicleProjection' value={item.projection} />
                        )) ||
                          "-"}
                        )
                      </Typography>
                    </Stack>
                  </Stack>
                ),
              },
              {
                field: "damageType.name",
                title: "Damage type",
                flex: 1,
                renderCell: (item) => item.damageType?.name,
              },
              {
                field: "damage.id",
                title: "Damage",
                flex: 1,
                renderCell: (item) =>
                  item.damage ? (
                    <VehicleDamageLink
                      vehicleId={item.damage?.vehicleId}
                      entity={item.damage}
                      inlineProps={{ withIcon: true, withMenu: true }}
                    />
                  ) : (
                    "-"
                  ),
              },
              {
                field: "notes",
                title: "Notes",
                width: 200,
                renderCell: (item) =>
                  item.notes ? (
                    <AppTooltip title={item.notes || ""}>
                      <AppTypography ellipsing={{ enabled: true }}>{item.notes}</AppTypography>
                    </AppTooltip>
                  ) : (
                    "-"
                  ),
              },
              {
                field: "attachments",
                title: "Attachments",
                width: 150,
                renderCell: (item) => (
                  <>
                    {item.attachments!.length !== 0 && (
                      <FileListView
                        files={FileItem.createManyFrom(item.attachments)}
                        maxVisibleFiles={2}
                        itemWidth={50}
                        itemHeight={30}
                        fileItemViewProps={{
                          sizeVariant: "compact",
                        }}
                      />
                    )}
                  </>
                ),
              },
            ]}
            rows={damageDetection.items!}
            rowHeight={65}
            getRowId={(item) => item.id!}
            borderVariant='bordered'
            isColumnSelectorEnabled={false}
            isColumnMenuEnabled={false}
            isPaginationEnabled={false}
            isWithScrollHelpers={false}
            renderRowAction={({ item }) => {
              return item.damage ? (
                <MenuWithTrigger
                  trigger={
                    <AppIconButton>
                      <AppIcon of='moreVert' />
                    </AppIconButton>
                  }
                >
                  {({ handleClose }) => [
                    <AppMenuItem
                      key={0}
                      permissionsAny={[AppPermission.VehicleDamageRead]}
                      onClick={async () => {
                        try {
                          const vehicleDamage = (
                            await apiClient.vehicleDamagesApi.apiV1VehiclesVehicleIdDamagesGetByDamageDetectionItemGet(
                              {
                                nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                                vehicleId: damageDetection.vehicle!.id!,
                                damageDetectionId: damageDetection.id!,
                                itemId: item.id!,
                              },
                            )
                          ).data;
                          if (vehicleDamage) {
                            history.push(
                              ROUTE_PATH.VEHICLE_DAMAGE_VIEW({
                                vehicleId: vehicleDamage.vehicle!.id!,
                                vehicleDamageId: vehicleDamage.id!,
                              }),
                            );
                          }
                        } catch (err) {
                          const validation2 = ValidationHelper.handleApiErrorResponse(err);
                          enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
                        }
                      }}
                    >
                      <ListItemIcon>
                        <AppIcon of='view' fontSize='small' />
                      </ListItemIcon>
                      <ListItemText>View damage</ListItemText>
                    </AppMenuItem>,
                    <AppMenuItem
                      key={1}
                      permissionsAny={[
                        AppPermission.RepairOperationPerform,
                        AppPermission.RepairOperationManage,
                      ]}
                      onClick={async () => {
                        try {
                          if (item.damage) {
                            setIsAddToRepairOperationModalOpen(true);
                            setDamageIdToAddToRepairOperation(item.damage.id);
                            handleClose();
                          }
                        } catch (err) {
                          const validation2 = ValidationHelper.handleApiErrorResponse(err);
                          enqueueSnackbar(validation2.getErrorsAsString(), { variant: "error" });
                        }
                      }}
                    >
                      <ListItemIcon>
                        <AppIcon of='add' fontSize='small' />
                      </ListItemIcon>
                      <ListItemText>Add damage to repair operation</ListItemText>
                    </AppMenuItem>,
                  ]}
                </MenuWithTrigger>
              ) : null;
            }}
          />
        </ViewContentBlock>

        {/* DCEs */}
        <ViewContentBlock>
          <AppTypography variant='h3'>Damage cost evaluations</AppTypography>

          <DamageDetectionCostEvaluationOverviewDisplay
            variant='normal'
            damageDetection={damageDetection}
            overview={damageDetection.costEvaluationOverview}
          />
        </ViewContentBlock>

        {/* T2T requests for DCE */}
        {/* <AuthorizedElement permissions={[AppPermission.TenantRequestRead]}>
          <ViewContentBlock>
            <AppTypography
              variant='h3'
              decoration={{ variant: "helpText" }}
              tooltipProps={{
                title:
                  "C2C requests for damage cost evaluation created and sent from this damage detection.",
                placement: "bottom-start",
              }}
            >
              External damage cost evaluations
            </AppTypography>

            <TenantRequestPaginatedList
              displayProps={{
                viewVariant: ViewLayoutVariant.Tab,
                breadcrumbs: false,
                counters: false,
                header: false,
                create: false,
              }}
              defaultValues={{
                limit: 10,
                source: {
                  sourceId: {
                    equal: damageDetection.id,
                  },
                  type: {
                    equal: EntitySourceType.DamageDetection,
                  },
                },
              }}
            />
          </ViewContentBlock>
        </AuthorizedElement> */}

        {/* Add to repair operation */}
        {damageDetection.vehicle?.id && damageIdToAddToRepairOperation && (
          <RepairOperationAddVehicleDamagesModal
            open={isAddToRepairOperationModalOpen}
            onClose={() => setIsAddToRepairOperationModalOpen(false)}
            vehicleId={damageDetection.vehicle.id}
            vehicleDamageIdsCosts={undefined}
            vehicleDamageIds={[damageIdToAddToRepairOperation]}
            onSaved={(newValue) => onUpdate && onUpdate(null)}
          />
        )}
      </ViewLayout>
    </PageTabContent>
  );
}
